/**
 * Etapa 1 — Identificação: qual é a máquina, os dados da placa e a situação.
 */
import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useCompanyList } from '../../../../../stores/companyStore';
import { CompanySelectField } from '../../../../../components/forms';
import { useListaPermitida } from '../../../../../authorization/useListaPermitida';
import { sourceGeneratorService, SourceGenerator } from '../../../../../services/sourceGeneratorService';
import { MachineStatus } from '../../../../../types/machine';
import type { Department } from '../../../../../types/department';
import {
  IdentificationErrors,
  IdentificationValues,
  MACHINE_STATUS_LABELS,
} from '../MachineWizard.types';

const fieldSx = { '& .MuiFormHelperText-root': { fontSize: '1rem' } };

/** Uma amostra do catálogo, só para saber se há tipos de equipamento cadastrados. */
const loadCatalogSample = async (): Promise<readonly SourceGenerator[]> =>
  (await sourceGeneratorService.getSourceGenerators({ limit: 1 })).sourceGenerators;

interface IdentificationStepProps {
  values: IdentificationValues;
  errors: IdentificationErrors;
  departments: Department[];
  /** Pergunta a empresa dona: só na criação, quando o cabeçalho não tem empresa ativa. */
  askCompany?: boolean;
  /** Máquina já salva: a empresa aparece, mas não muda mais. */
  companyLocked?: boolean;
  disabled?: boolean;
  onChange: (patch: Partial<IdentificationValues>) => void;
}

export const IdentificationStep: React.FC<IdentificationStepProps> = ({
  values,
  errors,
  departments,
  askCompany = false,
  companyLocked = false,
  disabled = false,
  onChange,
}) => {
  const [options, setOptions] = useState<SourceGenerator[]>([]);
  const [typeInput, setTypeInput] = useState(values.sourceGenerator?.name ?? '');

  // Descobre se há catálogo: sem ele, o tipo de equipamento nem é oferecido.
  const catalog = useListaPermitida('source_generator.read', 'Tipos de equipamento', loadCatalogSample);
  const catalogEmpty = catalog.resolvida && catalog.permitido && (catalog.itens.length === 0 || Boolean(catalog.erro));
  const catalogUnavailable = catalogEmpty || !catalog.permitido;

  useEffect(() => {
    if (catalogUnavailable) return undefined;
    if (values.sourceGenerator && typeInput === values.sourceGenerator.name) return undefined;
    let active = true;
    const timer = setTimeout(() => {
      sourceGeneratorService
        .getSourceGenerators({ search: typeInput || undefined, limit: 20 })
        .then((response) => {
          if (active) setOptions(response.sourceGenerators);
        })
        .catch(() => {
          if (active) setOptions([]);
        });
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [typeInput, catalogUnavailable, values.sourceGenerator]);

  const handleTypeChange = (generator: SourceGenerator | null) => {
    onChange({
      sourceGenerator: generator,
      manufacturer: generator?.manufacturer ?? values.manufacturer,
      model: generator?.model ?? values.model,
    });
  };

  // O campo de empresa só aparece com a lista pronta e mais de uma opção; uma empresa só o próprio campo preenche.
  const companyList = useCompanyList();
  const companyListFailed = askCompany && companyList.carregada && Boolean(companyList.error);
  const companyListLoading = askCompany && !companyList.carregada;
  const companyFieldVisible =
    askCompany && companyList.carregada && !companyList.error && companyList.companies.length !== 1;

  // Escolhida a empresa, só os setores dela servem; sem escolha (empresa ativa), vale a lista toda.
  const companyDepartments = values.companyId
    ? departments.filter((department) => String(department.companyId) === values.companyId)
    : departments;

  const hasMaintenanceData = Boolean(
    values.maintenanceFrequency || values.lastMaintenanceDate || values.nextMaintenanceDate
  );

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 3 }}>
        Diga qual é a máquina. {companyFieldVisible ? 'O nome e a empresa são obrigatórios' : 'Só o nome é obrigatório'}; o
        resto você pode completar depois.
      </Typography>

      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Qual é a máquina
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {companyListLoading && (
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} aria-label="Carregando as empresas" />
            <Typography variant="body1">Carregando as empresas...</Typography>
          </Grid>
        )}
        {companyListFailed && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={companyList.loadCompanies} sx={{ minHeight: 44 }}>
                  Tentar de novo
                </Button>
              }
            >
              Não foi possível carregar as empresas. Sem elas não dá para escolher a dona da máquina.
            </Alert>
          </Grid>
        )}
        {askCompany && !companyListFailed && (
          <CompanySelectField
            grid={{ xs: 12, md: 6 }}
            required
            label="Empresa proprietária"
            value={values.companyId}
            onChange={(companyId) => onChange({ companyId, departmentId: '' })}
            error={Boolean(errors.companyId)}
            helperText={
              errors.companyId ||
              (companyLocked
                ? 'A empresa não muda depois que a máquina é salva'
                : 'A máquina entra no inventário desta empresa.')
            }
            disabled={disabled || companyLocked}
          />
        )}
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            select
            fullWidth
            label="Setor"
            value={values.departmentId}
            onChange={(event) => onChange({ departmentId: event.target.value })}
            disabled={disabled}
            helperText="Opcional. Sem setor, a máquina só entra no laudo da empresa inteira."
            sx={fieldSx}
          >
            <MenuItem value="">Sem setor</MenuItem>
            {companyDepartments.map((department) => (
              <MenuItem key={department.id} value={String(department.id)}>
                {department.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            required
            label="Nome da máquina"
            value={values.name}
            onChange={(event) => onChange({ name: event.target.value })}
            error={Boolean(errors.name)}
            helperText={errors.name || 'Ex.: Prensa excêntrica 63 t'}
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Identificação interna"
            value={values.tag}
            onChange={(event) => onChange({ tag: event.target.value })}
            error={Boolean(errors.tag)}
            helperText={errors.tag || 'Plaqueta ou patrimônio da empresa'}
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          {!catalog.permitido ? (
            <Alert severity="info">
              {catalog.bloqueio} Tudo bem: o nome da máquina já a identifica.
            </Alert>
          ) : catalogEmpty ? (
            <Alert severity="info">
              Não há tipos de equipamento cadastrados em Fontes geradoras. Tudo bem: o nome da máquina já a
              identifica.
            </Alert>
          ) : (
            <Autocomplete
              options={options}
              value={values.sourceGenerator}
              inputValue={typeInput}
              onInputChange={(_, text) => setTypeInput(text)}
              onChange={(_, generator) => handleTypeChange(generator)}
              getOptionLabel={(generator) => generator.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              filterOptions={(all) => all}
              disabled={disabled}
              noOptionsText="Nenhum tipo encontrado"
              clearText="Limpar"
              openText="Abrir lista"
              closeText="Fechar lista"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tipo de equipamento"
                  helperText="Ao escolher o tipo, fabricante e modelo mais comuns são sugeridos. Você pode trocar."
                  sx={fieldSx}
                />
              )}
            />
          )}
        </Grid>
      </Grid>

      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Dados da placa
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Fabricante"
            value={values.manufacturer}
            onChange={(event) => onChange({ manufacturer: event.target.value })}
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Modelo"
            value={values.model}
            onChange={(event) => onChange({ model: event.target.value })}
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="Número de série"
            value={values.serialNumber}
            onChange={(event) => onChange({ serialNumber: event.target.value })}
            error={Boolean(errors.serialNumber)}
            helperText={errors.serialNumber}
            disabled={disabled}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            fullWidth
            label="Ano de fabricação"
            value={values.yearOfManufacture}
            onChange={(event) => onChange({ yearOfManufacture: event.target.value })}
            error={Boolean(errors.yearOfManufacture)}
            helperText={errors.yearOfManufacture}
            disabled={disabled}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            sx={fieldSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <TextField
            fullWidth
            label="Onde fica na planta"
            value={values.location}
            onChange={(event) => onChange({ location: event.target.value })}
            disabled={disabled}
            helperText="Ex.: Galpão 2, linha de estampagem, posição 4"
            sx={fieldSx}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
        Situação
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            select
            fullWidth
            label="Situação da máquina"
            value={values.status}
            onChange={(event) => onChange({ status: event.target.value as MachineStatus })}
            disabled={disabled}
            sx={fieldSx}
          >
            {Object.values(MachineStatus).map((status) => (
              <MenuItem key={status} value={status}>
                {MACHINE_STATUS_LABELS[status]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <TextField
            fullWidth
            label="Observações"
            value={values.observations}
            onChange={(event) => onChange({ observations: event.target.value })}
            disabled={disabled}
            helperText="Algo que o laudo deva registrar"
            sx={fieldSx}
          />
        </Grid>
      </Grid>

      <Accordion
        disableGutters
        elevation={0}
        defaultExpanded={hasMaintenanceData}
        sx={{ mt: 4, border: 1, borderColor: 'divider', '&::before': { display: 'none' } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          id="manutencao-header"
          aria-controls="manutencao-content"
        >
          <Typography variant="h6" component="h3">
            Manutenção (opcional)
          </Typography>
        </AccordionSummary>
        <AccordionDetails id="manutencao-content">
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Frequência de manutenção"
                value={values.maintenanceFrequency}
                onChange={(event) => onChange({ maintenanceFrequency: event.target.value })}
                error={Boolean(errors.maintenanceFrequency)}
                helperText={errors.maintenanceFrequency || 'Ex.: mensal, trimestral, anual'}
                disabled={disabled}
                sx={fieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Última manutenção"
                value={values.lastMaintenanceDate}
                onChange={(event) => onChange({ lastMaintenanceDate: event.target.value })}
                disabled={disabled}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={fieldSx}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Próxima manutenção"
                value={values.nextMaintenanceDate}
                onChange={(event) => onChange({ nextMaintenanceDate: event.target.value })}
                disabled={disabled}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default IdentificationStep;
